import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User } from '../types';
import { authService } from '../services';
import { User as ApiUser } from '../services/types/api';
import { trackEvent, trackFunnelEvent } from '../utils/analytics';

// Debug configuration - only enable in development with explicit flag
const DEBUG_AUTH_CONTEXT = import.meta.env.DEV &&
  (typeof localStorage !== 'undefined' && localStorage.getItem('debugAuthContext') === 'true');

const getSentryFunctions = async () => {
  const { setSentryUser, clearSentryUser, addSentryBreadcrumb, captureException, captureFunnelDeadEnd } = await import('../utils/sentry');
  return { setSentryUser, clearSentryUser, addSentryBreadcrumb, captureException, captureFunnelDeadEnd };
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  sessionExpiry: Date | null;
  isSessionExpiring: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, planIntent?: string, billingInterval?: string, marketingConsent?: boolean) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshSession: () => Promise<boolean>;
  extendSession: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);
  const [isSessionExpiring, setIsSessionExpiring] = useState(false);
  const initializationRef = useRef(false);

  // Helper function to convert backend user data to frontend format
  const convertBackendUserToFrontend = (backendUser: any): User => {
    const backendSubscription = backendUser.subscription;
    let subscriptionTier: 'basic' | 'pro' | 'pro_plus' | 'agency' | 'enterprise' = 'basic';

    // 🔍 DEBUG: Log backend user data structure
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔍 AUTH CONTEXT DEBUG - convertBackendUserToFrontend called:', {
        timestamp: new Date().toISOString(),
        hasBackendUser: !!backendUser,
        backendUserKeys: backendUser ? Object.keys(backendUser) : [],
        hasSubscription: !!backendSubscription,
        subscriptionKeys: backendSubscription ? Object.keys(backendSubscription) : [],
        analysesUsed: backendSubscription?.analyses_used,
        analysesLimit: backendSubscription?.analyses_limit,
        fullBackendUser: backendUser
      });
    }
    
    if (backendSubscription?.plan_name) {
      // Use the correct plan_name from subscriptions table
      switch (backendSubscription.plan_name) {
        case 'free':
          subscriptionTier = 'basic';
          break;
        case 'basic':
          subscriptionTier = 'basic';
          break;
        case 'pro':
          subscriptionTier = 'pro';
          break;
        case 'pro_plus':
          subscriptionTier = 'pro_plus';
          break;
        case 'agency':
          subscriptionTier = 'agency';
          break;
        case 'business':
          // Legacy alias — maps to agency tier for feature access
          subscriptionTier = 'agency';
          break;
        case 'enterprise':
          subscriptionTier = 'enterprise';
          break;
        default:
          subscriptionTier = 'basic';
      }
    } else if (backendUser.subscription_tier) {
      // Fallback to old subscription_tier field if subscription object not available
      switch (backendUser.subscription_tier) {
        case 'free':
          subscriptionTier = 'basic';
          break;
        case 'starter':
          subscriptionTier = 'basic';
          break;
        case 'professional':
          subscriptionTier = 'pro';
          break;
        default:
          subscriptionTier = 'basic';
      }
    }

    const frontendUser = {
      id: backendUser.id,
      email: backendUser.email,
      name: backendUser.name || 'User',
      subscription: {
        tier: subscriptionTier,
        // WB-46: enterprise is custom-priced. It showed an invented £299/month;
        // -1 is the backend's own "custom pricing" sentinel (routes/account).
        monthlyFee: subscriptionTier === 'basic' ? 9.95 :
                   subscriptionTier === 'pro' ? 19.95 :
                   subscriptionTier === 'pro_plus' ? 34.95 :
                   subscriptionTier === 'agency' ? 69.99 : -1,
        status: backendSubscription?.status || 'active',
        usageThisMonth: {
          analysesUsed: backendSubscription?.analyses_used || 0,
          analysesLimit: backendSubscription?.analyses_limit || 10,
          // Use monthly_keywords_used/limit first (current schema), fall back to keywords_tracked/limit (legacy)
          keywordsTracked: backendSubscription?.monthly_keywords_used ?? backendSubscription?.keywords_tracked ?? 0,
          keywordsLimit: backendSubscription?.monthly_keywords_limit ?? backendSubscription?.plan?.keywords_limit ?? backendSubscription?.keywords_limit ?? 100
        },
        // Pass through backend subscription data for ProjectsPage
        ...(backendSubscription && {
          plan_name: backendSubscription.plan_name,
          projects_limit: backendSubscription.projects_limit,
          projects_used: backendSubscription.projects_used,
          addon_projects: backendSubscription.addon_projects,
          trial_end: backendSubscription.trial_end,
          // Pass through the full plan object with limits for feature access control
          plan: backendSubscription.plan,
          // Strategy Engine usage tracking
          monthly_strategy_analyses_used: backendSubscription.monthly_strategy_analyses_used ?? 0,
          monthly_strategy_analyses_limit: backendSubscription.monthly_strategy_analyses_limit ?? 2
        })
      },
      preferences: {
        theme: 'light' as const,
        notifications: true,
        dashboardLayout: 'default'
      }
    };

    // 🔍 DEBUG: Log converted frontend user
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔍 AUTH CONTEXT DEBUG - Frontend user created:', {
        timestamp: new Date().toISOString(),
        analysesUsed: frontendUser.subscription.usageThisMonth.analysesUsed,
        analysesLimit: frontendUser.subscription.usageThisMonth.analysesLimit,
        keywordsTracked: frontendUser.subscription.usageThisMonth.keywordsTracked,
        keywordsLimit: frontendUser.subscription.usageThisMonth.keywordsLimit,
        fullFrontendUser: frontendUser
      });
    }

    return frontendUser;
  };

  useEffect(() => {
    if (initializationRef.current) {
      return;
    }
    initializationRef.current = true;

    const initializeAuth = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const legacyToken = localStorage.getItem('authToken');

        // Public auth pages where ?token= is NOT a session token — don't hijack it
        const publicAuthPaths = ['/reset-password', '/forgot-password', '/login', '/register'];
        const isPublicAuthPage = publicAuthPaths.some(p => window.location.pathname.startsWith(p));

        // 🔍 DEBUG: Log current token storage state
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔐 AUTH CONTEXT - Token storage state:', {
            urlToken: !!urlToken,
            isPublicAuthPage,
            legacyToken: !!legacyToken,
            authToken: !!localStorage.getItem('authToken'),
            refreshToken: !!localStorage.getItem('refreshToken'),
            access_token: !!localStorage.getItem('access_token'),
            rnkrocket_auth_token: !!localStorage.getItem('rnkrocket_auth_token'),
            timestamp: new Date().toISOString()
          });
        }

        if (urlToken && !isPublicAuthPage) {
          // Only consume ?token= as auth token on non-public pages
          if (DEBUG_AUTH_CONTEXT) {
            console.log('🔐 AUTH CONTEXT - Setting tokens from URL parameter');
          }
          localStorage.setItem('authToken', urlToken);
          // A URL-token session has no refresh handle. Remove any stored
          // refreshToken (it belongs to a previous session, possibly a
          // different user) rather than overwriting it with the access
          // token — /auth/refresh rejects access tokens (S3.5), so an
          // overwrite guarantees a hard logout at access-token expiry.
          localStorage.removeItem('refreshToken');
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          window.history.replaceState({}, '', newUrl.toString());
        }
        // Deliberately NO else-branch for an existing authToken: the old code
        // copied authToken over refreshToken here — a single-token-era
        // leftover that, once S3.5 split tokens (15-min access / 30-day
        // rotating refresh), destroyed the real refresh token on every page
        // load and capped every session at 15 minutes (2026-07-13 incident).
        // The login and refresh flows own these keys; boot must not touch
        // refreshToken.

        if (authService.isAuthenticated()) {
          // 🔧 FIX: Ensure apiClient headers are restored before making API calls
          const authToken = localStorage.getItem('authToken');
          const refreshToken = localStorage.getItem('refreshToken');

          if (DEBUG_AUTH_CONTEXT) {
            console.log('🔐 AUTH CONTEXT - Pre-API call token state:', {
              hasAuthToken: !!authToken,
              hasRefreshToken: !!refreshToken,
              authTokenLength: authToken?.length || 0,
              refreshTokenLength: refreshToken?.length || 0,
              authTokenPreview: authToken ? `${authToken.substring(0, 10)}...` : 'none'
            });
          }

          if (authToken) {
            if (DEBUG_AUTH_CONTEXT) {
              console.log('🔐 AUTH CONTEXT - Restoring apiClient auth headers on initialization');
            }
            // Import apiClient and restore its auth state
            const { apiClient } = await import('../services/apiClient');
            apiClient.setAuthTokens(authToken, refreshToken || authToken);

            // 🔍 VALIDATION: Verify headers were set correctly
            if (DEBUG_AUTH_CONTEXT) {
              const config = apiClient.getConfig();
              console.log('🔐 AUTH CONTEXT - ApiClient config after setAuthTokens:', {
                baseURL: config.baseURL,
                hasAuthHeader: !!(apiClient as any).client.defaults.headers.common['Authorization'],
                authHeaderPreview: (apiClient as any).client.defaults.headers.common['Authorization'] ?
                  `${(apiClient as any).client.defaults.headers.common['Authorization'].substring(0, 20)}...` : 'none'
              });
            }

            // 🔍 VALIDATION: Wait a moment to ensure headers are propagated
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          if (DEBUG_AUTH_CONTEXT) {
            console.log('🔐 AUTH CONTEXT - Making getCurrentUser API call...');
          }
          const apiUser = await authService.getCurrentUser();
          if (apiUser) {
            const frontendUser = convertBackendUserToFrontend(apiUser as any);

            // Trial-first: we no longer auto-resume-checkout for pending_payment
            // users. Under the old pay-first flow this was the "if you bounced
            // from Stripe we send you back" path; under trial-first, signup
            // doesn't touch Stripe, so any lingering pending_payment sub is
            // legacy data that gets self-healed to 'active' on login. We still
            // clear the checkout return flag here so post-checkout state
            // doesn't persist across sessions.
            sessionStorage.removeItem('stripe_checkout_return');

            if (DEBUG_AUTH_CONTEXT) {
              console.log('🔍 AUTH CONTEXT DEBUG - Setting user state during initialization:', {
                timestamp: new Date().toISOString(),
                analysesUsed: frontendUser.subscription.usageThisMonth.analysesUsed,
                analysesLimit: frontendUser.subscription.usageThisMonth.analysesLimit,
                source: 'initialization'
              });
            }
            setUser(frontendUser);
            const { setSentryUser, addSentryBreadcrumb } = await getSentryFunctions();
            setSentryUser({
              id: frontendUser.id,
              email: frontendUser.email,
              username: frontendUser.name,
              subscription: frontendUser.subscription.tier,
            });
            addSentryBreadcrumb('User authenticated on app initialization', 'auth', 'info');
          } else {
            await logout();
          }
        }
      } catch (error) {
        // 429 = rate limited — tokens are still valid, don't logout.
        // Schedule a silent retry so the user isn't locked out of the app.
        const axiosError = error as any;
        const status = axiosError?.response?.status ?? axiosError?.status;
        if (status === 429) {
          console.warn('[AUTH] Rate limited on /auth/me — retrying in 5s');
          setTimeout(async () => {
            try {
              const apiUser = await authService.getCurrentUser();
              if (apiUser) {
                setUser(convertBackendUserToFrontend(apiUser as any));
              }
            } catch (retryErr) {
              console.warn('[AUTH] Retry after 429 failed:', retryErr);
            }
          }, 5000);
        } else if (error instanceof Error && error.message.includes('401')) {
          // Access token expired (common when returning from Stripe checkout).
          // Try a silent refresh with the stored refresh token BEFORE logging the
          // user out — surfaces the S3.5 refresh-token flow at boot so a returning
          // user with a valid refresh token keeps their session.
          try {
            const newAccess = await authService.refreshToken();
            if (newAccess) {
              const { apiClient } = await import('../services/apiClient');
              apiClient.setAuthTokens(newAccess, localStorage.getItem('refreshToken') || newAccess);
              const apiUser = await authService.getCurrentUser();
              if (apiUser) {
                setUser(convertBackendUserToFrontend(apiUser as any));
                sessionStorage.removeItem('stripe_checkout_return');
              } else {
                await logout();
              }
            } else {
              await logout();
            }
          } catch (refreshErr) {
            // Refresh token also dead — the session is genuinely gone.
            await logout();
          }
        } else {
          const { captureException } = await getSentryFunctions();
          captureException(error as Error, { tags: { source: 'initializeAuth' } });
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Session monitoring and expiry detection
  useEffect(() => {
    if (!user) return;

    // Check for existing session expiry only if sessionExpiry is not already set
    if (!sessionExpiry) {
      const storedExpiry = localStorage.getItem('sessionExpiry');
      if (storedExpiry) {
        const expiryDate = new Date(storedExpiry);
        setSessionExpiry(expiryDate);
        
        if (expiryDate <= new Date()) {
          logout();
          return;
        }
      }
    }

    // Set up session expiry monitoring
    const checkSessionExpiry = () => {
      if (!sessionExpiry) return;
      
      const now = new Date();
      const timeUntilExpiry = sessionExpiry.getTime() - now.getTime();
      const fiveMinutes = 5 * 60 * 1000;

      if (timeUntilExpiry <= 0) {
        logout();
      } else if (timeUntilExpiry <= fiveMinutes && !isSessionExpiring) {
        setIsSessionExpiring(true);
        window.dispatchEvent(new CustomEvent('auth:session-expiring', {
          detail: {
            expiresAt: sessionExpiry,
            timeRemaining: timeUntilExpiry
          }
        }));
      }
    };

    // Check immediately
    checkSessionExpiry();
    
    // Set up interval to check every minute
    const interval = setInterval(checkSessionExpiry, 60000);
    
    return () => clearInterval(interval);
  }, [user, sessionExpiry, isSessionExpiring]);

  // Listen for auth events from API client
  useEffect(() => {
    const handleSessionExpired = async (event: Event) => {
      setUser(null);
      setSessionExpiry(null);
      setIsSessionExpiring(false);
      try {
        const { clearSentryUser } = await getSentryFunctions();
        clearSentryUser();
      } catch (sentryError) {
        console.warn('Failed to clear Sentry user:', sentryError);
      }
    };

    const handleSessionExpiring = (event: Event) => {
      setIsSessionExpiring(true);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('auth:session-expiring', handleSessionExpiring);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('auth:session-expiring', handleSessionExpiring);
    };
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });

      // If backend returned a checkoutUrl (pending_payment user), redirect to Stripe
      const checkoutUrl = (response as any).checkoutUrl;
      if (checkoutUrl) {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔐 Redirecting pending_payment user to Stripe Checkout:', checkoutUrl);
        }
        trackEvent('begin_checkout', { source: 'login_pending' });
        trackFunnelEvent('begin_checkout', '/login', 'login_pending');
        window.location.href = checkoutUrl;
        return; // Don't setUser — page is navigating away
      }

      // Fetch full profile (with accurate tier/subscription) before setting user state.
      // This avoids a brief flash where the nav shows 'basic' before the real tier loads.
      let fullProfile: any = null;
      try {
        fullProfile = await authService.getCurrentUser();
      } catch {
        // Fall back to login response data if /auth/me fails
      }

      const frontendUser = convertBackendUserToFrontend((fullProfile || response.user) as any);
      setUser(frontendUser);

      const expiryTime = new Date();
      if (rememberMe) {
        expiryTime.setDate(expiryTime.getDate() + 30);
        localStorage.setItem('rememberMe', 'true');
      } else {
        expiryTime.setHours(expiryTime.getHours() + 8);
        localStorage.removeItem('rememberMe');
      }
      setSessionExpiry(expiryTime);
      localStorage.setItem('sessionExpiry', expiryTime.toISOString());

      const { setSentryUser, addSentryBreadcrumb } = await getSentryFunctions();
      setSentryUser({
        id: frontendUser.id,
        email: frontendUser.email,
        username: frontendUser.name,
        subscription: frontendUser.subscription.tier,
      });
      addSentryBreadcrumb('User logged in successfully', 'auth', 'info');
    } catch (error) {
      // Surface rate-limit details so the login form can show "wait X minutes"
      const axiosError = error as any;
      const status = axiosError?.response?.status ?? axiosError?.status;
      if (status === 429) {
        const retryData = axiosError?.response?.data?.data;
        const minutes = retryData?.retryAfterMinutes || 1;
        throw new Error(`Too many login attempts. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} and try again.`);
      }
      const { captureException } = await getSentryFunctions();
      captureException(error as Error, { tags: { source: 'login' } });
      throw new Error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    planIntent?: string,
    billingInterval?: string,
    /** Opt-out form: `true` = user wants marketing, `false` = opted out.
     *  Sent as `marketing_consent` to the backend (affirmative form). */
    marketingConsent?: boolean,
  ) => {
    setIsLoading(true);
    try {
      // Terms acceptance is enforced by RegisterForm UI AND by the backend
      // (returns 400 TERMS_NOT_ACCEPTED if missing). RegisterForm only calls
      // this after the user has ticked the mandatory checkbox, so we send
      // `terms_accepted: true` unconditionally; the backend stores the
      // timestamp + version against the user row.
      const response = await authService.register({
        email,
        password,
        password_confirmation: password,
        first_name: firstName,
        last_name: lastName,
        terms_accepted: true,
        ...(planIntent && { plan_intent: planIntent }),
        ...(billingInterval && { billing_interval: billingInterval }),
        ...(typeof marketingConsent === 'boolean' && { marketing_consent: marketingConsent }),
      });

      // Trial-first signup: backend no longer returns a checkoutUrl on
      // register. We sign the user in immediately and let the post-login
      // flow land them in onboarding so they can add a domain and start
      // the 14-day trial. (Legacy `pending_payment` users still resume
      // their checkout via the LOGIN flow above, not register.)
      let fullProfile: any = null;
      try {
        fullProfile = await authService.getCurrentUser();
      } catch {
        // Fall back to register response data if /auth/me fails
      }

      const frontendUser = convertBackendUserToFrontend((fullProfile || response.user) as any);
      setUser(frontendUser);

      // Set session expiry for new users (8 hour default session)
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 8);
      setSessionExpiry(expiryTime);
      localStorage.setItem('sessionExpiry', expiryTime.toISOString());

      // Set Sentry user context for error tracking
      try {
        const { setSentryUser, addSentryBreadcrumb } = await getSentryFunctions();
        setSentryUser({
          id: frontendUser.id,
          email: frontendUser.email,
          username: frontendUser.name,
          subscription: frontendUser.subscription.tier
        });
        addSentryBreadcrumb('User registered successfully', 'auth', 'info');
      } catch (sentryError) {
        console.warn('Failed to set Sentry context on registration:', sentryError);
      }

      if (DEBUG_AUTH_CONTEXT) {
        console.log('✅ Registration successful, user state set.');
      }
    } catch (error) {
      const { captureException } = await getSentryFunctions();
      captureException(error as Error, { tags: { source: 'register' } });

      // Pass through the actual error message instead of masking it
      throw error instanceof Error ? error : new Error('Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      try {
        const { addSentryBreadcrumb } = await getSentryFunctions();
        addSentryBreadcrumb('User logout initiated', 'auth', 'info');
      } catch (sentryError) {
        console.warn('Failed to log Sentry breadcrumb on logout:', sentryError);
      }
      
      // Clear local state first to prevent UI flicker
      setUser(null);
      setSessionExpiry(null);
      setIsSessionExpiring(false);
      
      // Clear all auth-related storage
      const authKeys = ['authToken', 'refreshToken', 'sessionExpiry', 'rememberMe'];
      authKeys.forEach(key => localStorage.removeItem(key));
      
      // Clear Sentry user context
      try {
        const { clearSentryUser } = await getSentryFunctions();
        clearSentryUser();
      } catch (sentryError) {
        console.warn('Failed to clear Sentry user on logout:', sentryError);
      }
      
      // Attempt backend logout (non-blocking)
      try {
        await authService.logout();
        try {
          const { addSentryBreadcrumb } = await getSentryFunctions();
          addSentryBreadcrumb('User logged out successfully', 'auth', 'info');
        } catch (sentryError) {
          console.warn('Failed to log successful logout breadcrumb:', sentryError);
        }
      } catch (logoutError) {
        console.warn('Backend logout failed, but local state cleared:', logoutError);
        try {
          const { addSentryBreadcrumb } = await getSentryFunctions();
          addSentryBreadcrumb('Backend logout failed but local state cleared', 'auth', 'warning');
        } catch (sentryError) {
          console.warn('Failed to log logout error breadcrumb:', sentryError);
        }
      }
      
    } catch (error) {
      console.error('Logout process failed:', error);
      try {
        const { addSentryBreadcrumb } = await getSentryFunctions();
        addSentryBreadcrumb('Logout failed but clearing local state', 'auth', 'warning');
      } catch (sentryError) {
        console.warn('Failed to log logout failure breadcrumb:', sentryError);
      }
      
      // Ensure local state is cleared even if everything fails
      setUser(null);
      setSessionExpiry(null);
      setIsSessionExpiring(false);
      
      const authKeys = ['authToken', 'refreshToken', 'sessionExpiry', 'rememberMe'];
      authKeys.forEach(key => localStorage.removeItem(key));
      
      try {
        const { clearSentryUser } = await getSentryFunctions();
        clearSentryUser();
      } catch (sentryError) {
        console.warn('Failed to clear Sentry user on logout error:', sentryError);
      }
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    if (!user) {
      return false;
    }
    try {
      if (!authService.isAuthenticated()) {
        return false;
      }
      
      const apiUser = await authService.getCurrentUser();
      
      if (apiUser) {
        const frontendUser = convertBackendUserToFrontend(apiUser as any);
        setUser(frontendUser);
        
        // Reset session expiring state
        setIsSessionExpiring(false);
        
        // Extend session expiry
        const expiryTime = new Date();
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        if (rememberMe) {
          expiryTime.setDate(expiryTime.getDate() + 30);
        } else {
          expiryTime.setHours(expiryTime.getHours() + 8);
        }
        setSessionExpiry(expiryTime);
        localStorage.setItem('sessionExpiry', expiryTime.toISOString());
        
        // Update Sentry user context
        try {
          const { setSentryUser, addSentryBreadcrumb } = await getSentryFunctions();
          setSentryUser({
            id: frontendUser.id,
            email: frontendUser.email,
            username: frontendUser.name,
            subscription: frontendUser.subscription.tier
          });
          addSentryBreadcrumb('Session refreshed successfully', 'auth', 'info');
        } catch (sentryError) {
          console.warn('Failed to set Sentry context on session refresh:', sentryError);
        }

        if (DEBUG_AUTH_CONTEXT) {
          console.log('✅ Session refreshed successfully');
        }
        return true;
      }

      if (DEBUG_AUTH_CONTEXT) {
        console.warn('⚠️ Session refresh failed - no user data');
      }
      try {
        const { addSentryBreadcrumb } = await getSentryFunctions();
        addSentryBreadcrumb('Session refresh failed - no user data', 'auth', 'warning');
      } catch (sentryError) {
        console.warn('Failed to log session refresh failure breadcrumb:', sentryError);
      }
      return false;
    } catch (error) {
      // 429 = rate limited — session is still valid, just throttled. Don't logout.
      const axiosError = error as any;
      const status = axiosError?.response?.status ?? axiosError?.status;
      if (status === 429) {
        console.warn('[AUTH] Rate limited during session refresh — keeping session');
        return false;
      }

      console.error('❌ Session refresh failed:', error);
      try {
        const { addSentryBreadcrumb } = await getSentryFunctions();
        addSentryBreadcrumb('Session refresh failed with error', 'auth', 'error');
      } catch (sentryError) {
        console.warn('Failed to log session refresh error breadcrumb:', sentryError);
      }

      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
        await logout();
      }

      return false;
    }
  };

  const extendSession = async () => {
    await refreshSession();
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      sessionExpiry,
      isSessionExpiring,
      login,
      register,
      logout,
      updateUser,
      refreshSession,
      extendSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};